import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { autoUpdate, flip, offset, shift, size, useFloating } from '@floating-ui/react-dom';
import { List } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { CanvasReportedRegion } from '../hooks/use-storefront-canvas-host';
import { useWebsiteT } from '../hooks/use-website-t';

export interface EditableRegionsMenuProps {
  readonly regions: readonly CanvasReportedRegion[];
  readonly disabled: boolean;
  readonly onEditRegion: (regionId: string) => void;
}

/** Global editor actions live in the Admin chrome, outside the storefront document. */
export function EditableRegionsMenu({
  regions,
  disabled,
  onEditRegion,
}: EditableRegionsMenuProps): React.JSX.Element {
  const { t, dir } = useWebsiteT();
  const [open, setOpen] = useState(false);
  const id = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const lastOnOpenRef = useRef(false);
  // Drawn offscreen fields belong to their own part of the page. Missing and
  // CSS-hidden fields remain available without pretending they have a marker.
  const items = regions.filter((region) => !region.anchored || region.visible);
  const unavailable = disabled || items.length === 0;
  const expanded = open && !unavailable;
  const { refs, floatingStyles, isPositioned } = useFloating({
    open: expanded,
    placement: 'bottom-end',
    strategy: 'fixed',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(4),
      flip({ padding: 16 }),
      shift({ padding: 16 }),
      size({
        padding: 16,
        apply({ availableHeight, elements }) {
          elements.floating.style.maxHeight = `${Math.max(0, Math.min(384, availableHeight))}px`;
        },
      }),
    ],
  });
  const { setReference } = refs;
  const setTriggerRef = useCallback(
    (element: HTMLButtonElement | null): void => {
      triggerRef.current = element;
      setReference(element);
    },
    [setReference],
  );

  const menuItems = (): HTMLButtonElement[] =>
    Array.from(rootRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]') ?? []);

  useEffect(() => {
    if (!expanded || !isPositioned) return undefined;
    const buttons = menuItems();
    (lastOnOpenRef.current ? buttons[buttons.length - 1] : buttons[0])?.focus();
    const onPointerDown = (event: PointerEvent): void => {
      if (event.target instanceof Node && !rootRef.current?.contains(event.target)) setOpen(false);
    };
    // Pointer events inside the storefront iframe do not bubble to Admin.
    const onWindowBlur = (): void => setOpen(false);
    document.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('blur', onWindowBlur);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('blur', onWindowBlur);
    };
  }, [expanded, isPositioned]);

  useEffect(() => {
    if (unavailable) setOpen(false);
  }, [unavailable]);

  const close = (returnFocus: boolean): void => {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Escape') {
      event.preventDefault();
      close(true);
      return;
    }
    if (event.key === 'Tab') {
      close(false);
      return;
    }
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key) || unavailable) return;
    event.preventDefault();
    if (!expanded) {
      lastOnOpenRef.current = event.key === 'ArrowUp' || event.key === 'End';
      setOpen(true);
      return;
    }
    const buttons = menuItems();
    const current = buttons.findIndex((button) => button === document.activeElement);
    const next =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? buttons.length - 1
          : (current + (event.key === 'ArrowDown' ? 1 : -1) + buttons.length) % buttons.length;
    buttons[next]?.focus();
  };

  return (
    <div ref={rootRef} dir={dir} onKeyDown={onKeyDown} className="relative">
      <Button
        ref={setTriggerRef}
        type="button"
        variant="outline"
        className="min-h-11 min-w-11"
        disabled={unavailable}
        aria-haspopup="menu"
        aria-expanded={expanded}
        aria-controls={expanded ? id : undefined}
        data-testid="editable-regions-trigger"
        onClick={() => {
          lastOnOpenRef.current = false;
          setOpen((current) => !current);
        }}
      >
        <List aria-hidden="true" className="size-4" />
        {t('admin.websiteTemplates.overlay.moreItems')}
      </Button>
      {expanded ? (
        <div
          ref={refs.setFloating}
          id={id}
          role="menu"
          aria-label={t('admin.websiteTemplates.overlay.regionListTitle')}
          data-testid="editable-regions-menu"
          style={{ ...floatingStyles, visibility: isPositioned ? 'visible' : 'hidden' }}
          className="border-border bg-popover text-popover-foreground shadow-admin-elevated z-40 w-80 max-w-[calc(100vw-2rem)] overflow-y-auto rounded-xl border p-1"
        >
          {items.map((region) => (
            <button
              key={region.regionId}
              type="button"
              role="menuitem"
              data-editable-region={region.regionId}
              className="hover:bg-admin-surface-muted focus-visible:bg-admin-surface-muted block min-h-11 w-full break-words rounded-md px-3 py-2 text-start text-sm outline-none"
              onClick={() => {
                close(true);
                onEditRegion(region.regionId);
              }}
            >
              {region.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
